import type { TFunction } from 'i18next';
import type { ActiveGraphKind, NodeFlag } from '../graphTypes';
import { CASE_GUIDE_GROUPS } from './guidePages.case';
import { actorGuideGroups } from './guidePages.actor';

// Graph guide content (Figma "Network graph", flows 1150-2 case / 1151-2
// actor group / 1151-3 single actor, 2026-09). Three variants of the guide,
// one per graph type: a case graph, an actor-group graph and a single-actor
// explore graph. Every string is an i18n key (English copy = key); the panel
// translates at render time and the search index is built from the same
// translated text so a uk reader can search in uk.
//
// Inline markup inside the copy — translators keep it verbatim:
//   **Actors**                        a UI label quoted verbatim, rendered bold
//   *any*                             an emphasised word (bold, body colour)
//   [[how-to-read|text]]              a deep link into another guide page
//   [[calculations#pagerank|text]]    …or a section (block / row id) on it

export type GuideVariant = 'case' | 'actor-group' | 'actor-single';

export type GuidePageId =
  | 'welcome'
  | 'uc-communities'
  | 'uc-spread'
  | 'uc-roles'
  | 'uc-mixed'
  | 'uc-hra'
  | 'uc-strong-ties'
  | 'uc-period'
  | 'uc-handshakes'
  | 'uc-cross-platform'
  | 'uc-closest'
  | 'uc-mutual'
  | 'configuration'
  | 'filtering'
  | 'how-to-read'
  | 'calculations'
  | 'limitations';

export type GuideRow = {
  // section anchor ("Learn more" target)
  id?: string;
  key: string;
  text: string;
  // the optional emphasised rule line ("More connections = a larger node.")
  rule?: string;
  // the optional muted line(s) under the value
  why?: string | string[];
};

// a numbered step: the instruction and an optional muted explanation
export type GuideStep = { text: string; why?: string };

// a tile / card on the Getting started page: a page to open, its title and blurb
export type GuideLink = { page: GuidePageId; title: string; text: string };

// "Learn more" footer entry: a page, optionally a section on it; the label is
// the section's own title, or the page title when none is given
export type GuideRelated = {
  page: GuidePageId;
  section?: string;
  title?: string;
};

export type GuideBlock =
  | { type: 'p'; text: string; id?: string }
  | { type: 'h'; text: string; id?: string }
  | { type: 'note'; text: string; id?: string }
  // numbered step cards
  | { type: 'steps'; items: (string | GuideStep)[] }
  | { type: 'list'; items: string[] }
  | { type: 'rows'; rows: GuideRow[] }
  | { type: 'tiles'; items: GuideLink[] }
  | { type: 'cards'; items: GuideLink[] }
  | { type: 'related'; items: GuideRelated[] };

export const guideStep = (item: string | GuideStep): GuideStep =>
  typeof item === 'string' ? { text: item } : item;

export const guideRowWhy = (row: GuideRow): string[] =>
  row.why === undefined ? [] : Array.isArray(row.why) ? row.why : [row.why];

export type GuidePage = {
  id: GuidePageId;
  title: string;
  // shorter label for the table of contents
  navTitle?: string;
  blocks: GuideBlock[];
};

export type GuideGroup = {
  // '' = the ungrouped top-level entry (Getting started)
  label: string;
  pages: GuidePage[];
};

export const DEFAULT_GUIDE_PAGE: GuidePageId = 'welcome';

// Where a hint's "Learn more" lands: a page, optionally a section anchor on it
// (`id` of a block or table row). Unknown sections leave the reader at the
// page top, so a stale id degrades gracefully.
export type GuideTarget = { page: GuidePageId; section?: string };

export const getGuideGroups = (variant: GuideVariant): GuideGroup[] =>
  variant === 'case' ? CASE_GUIDE_GROUPS : actorGuideGroups(variant);

export const getGuidePages = (variant: GuideVariant): GuidePage[] =>
  getGuideGroups(variant).flatMap((group) => group.pages);

// the page, or Getting started when the id is not in this variant (the page
// is remembered across graphs, and the three guides share only some ids)
export const getGuidePage = (
  variant: GuideVariant,
  id: GuidePageId,
): GuidePage => {
  const pages = getGuidePages(variant);
  return pages.find((page) => page.id === id) ?? pages[0];
};

export const guideGroupOf = (variant: GuideVariant, id: GuidePageId): string =>
  getGuideGroups(variant).find((group) =>
    group.pages.some((page) => page.id === id),
  )?.label ?? '';

// which guide a graph reads: case graphs, actor-group graphs (group / topic)
// and single-actor explore graphs each get their own copy
export const guideVariantFor = (
  kind: ActiveGraphKind,
  isExploreGraph: boolean,
): GuideVariant =>
  kind === 'case' ? 'case' : isExploreGraph ? 'actor-single' : 'actor-group';

// DOM id of a section anchor inside the rendered panel
export const guideSectionDomId = (page: GuidePageId, section: string) =>
  `graphGuide-${page}-${section}`;

// DOM id of the Nth search result (aria-activedescendant of the search box)
export const guideSearchHitDomId = (listId: string, index: number) =>
  `${listId}-hit-${index}`;

// Section anchor a search hit lands on: every block is addressable by index
export const guideBlockSection = (index: number) => `block-${index}`;

const blockSectionIds = (block: GuideBlock, index: number): string[] => {
  const ids = [guideBlockSection(index)];
  if ('id' in block && block.id) ids.push(block.id);
  if (block.type === 'rows')
    block.rows.forEach((row) => row.id && ids.push(row.id));
  return ids;
};

// every anchor a "Learn more" / search hit may point at on a page
export const guidePageSectionIds = (page: GuidePage): string[] =>
  page.blocks.flatMap(blockSectionIds);

export const guideHasSection = (
  variant: GuideVariant,
  target: GuideTarget,
): boolean => {
  const page = getGuidePages(variant).find((entry) => entry.id === target.page);
  if (!page) return false;
  if (!target.section) return true;
  return guidePageSectionIds(page).includes(target.section);
};

// Actors-tab category rows → the matching row of the Categories table on
// How to read the graph. Roles without a row of their own (the backend's
// hidden / z-score-only roles) land on the Categories heading instead.
export const actorRoleSection = (
  role: string,
  graphKind: ActiveGraphKind,
): string => {
  const slug = role
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const variant = guideVariantFor(graphKind, false);
  return guideHasSection(variant, { page: 'how-to-read', section: slug })
    ? slug
    : 'categories';
};

// Node flag rows (High Risk / State Affiliated / Critical bridge /
// High-Interaction) → their row in the Categories table; the actor guides list
// no State Affiliated row, so that flag lands on the heading there.
export const nodeFlagSection = (
  flag: NodeFlag,
  graphKind: ActiveGraphKind,
): GuideTarget => {
  const section: Record<NodeFlag, string> = {
    highRisk: 'high-risk',
    stateAffiliated: 'state-affiliated',
    connector: 'critical-bridge',
    highInteraction: 'high-interaction',
  };
  const variant = guideVariantFor(graphKind, false);
  return {
    page: 'how-to-read',
    section: guideHasSection(variant, {
      page: 'how-to-read',
      section: section[flag],
    })
      ? section[flag]
      : 'categories',
  };
};

// ---------------------------------------------------------------------------
// Inline markup

export type GuideInlineNode =
  | string
  | { type: 'ui'; text: string }
  | { type: 'em'; text: string }
  | {
      type: 'xref';
      page: GuidePageId;
      section?: string;
      children: GuideInlineNode[];
    };

const UI_LABEL = /\*\*(.+?)\*\*/g;
// a single-starred word; never crosses a UI label (those are cut out first)
const EMPHASIS = /\*([^*]+?)\*/g;
// [[page|text]] or [[page#section|text]]
const XREF = /\[\[([\w-]+)(?:#([\w-]+))?\|(.+?)\]\]/g;

const parseEmphasis = (text: string): GuideInlineNode[] => {
  const nodes: GuideInlineNode[] = [];
  let last = 0;
  text.replace(EMPHASIS, (match, word: string, offset: number) => {
    if (offset > last) nodes.push(text.slice(last, offset));
    nodes.push({ type: 'em', text: word });
    last = offset + match.length;
    return match;
  });
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const parseUiLabels = (text: string): GuideInlineNode[] => {
  const nodes: GuideInlineNode[] = [];
  let last = 0;
  text.replace(UI_LABEL, (match, label: string, offset: number) => {
    if (offset > last) nodes.push(...parseEmphasis(text.slice(last, offset)));
    nodes.push({ type: 'ui', text: label });
    last = offset + match.length;
    return match;
  });
  if (last < text.length) nodes.push(...parseEmphasis(text.slice(last)));
  return nodes;
};

// "**Actors** panel in [[configuration|**Configuration**]]" → nodes; a page
// link may itself contain a UI label, a UI label never contains a link
export const parseGuideInline = (text: string): GuideInlineNode[] => {
  const nodes: GuideInlineNode[] = [];
  let last = 0;
  text.replace(
    XREF,
    (
      match,
      page: string,
      section: string | undefined,
      inner: string,
      offset: number,
    ) => {
      if (offset > last) nodes.push(...parseUiLabels(text.slice(last, offset)));
      nodes.push({
        type: 'xref',
        page: page as GuidePageId,
        ...(section ? { section } : {}),
        children: parseUiLabels(inner),
      });
      last = offset + match.length;
      return match;
    },
  );
  if (last < text.length) nodes.push(...parseUiLabels(text.slice(last)));
  return nodes;
};

// the copy without its markup — for the search index and accessible names
export const guidePlainText = (text: string): string =>
  text.replace(XREF, '$3').replace(UI_LABEL, '$1').replace(EMPHASIS, '$1');

// ---------------------------------------------------------------------------
// Search: runs over single lines, not whole articles — a result names the
// page and shows the line that matched, the way a docs search does.

export type GuideIndexEntry = {
  page: GuidePageId;
  pageTitle: string;
  // -1 = the page title itself
  block: number;
  text: string;
  // 0 title, 1 heading, 2 body — better ranks list first
  rank: 0 | 1 | 2;
};

export type GuideSearchHit = GuideIndexEntry & { position: number };

const normalise = (text: string) =>
  guidePlainText(text).replace(/\s+/g, ' ').trim();

export const buildGuideIndex = (
  pages: GuidePage[],
  t: TFunction,
): GuideIndexEntry[] => {
  const entries: GuideIndexEntry[] = [];
  pages.forEach((page) => {
    const pageTitle = t(page.title);
    const add = (text: string, block: number, rank: 0 | 1 | 2) => {
      const line = normalise(t(text));
      if (line)
        entries.push({ page: page.id, pageTitle, block, text: line, rank });
    };
    add(page.title, -1, 0);
    page.blocks.forEach((block, index) => {
      switch (block.type) {
        case 'h':
          add(block.text, index, 1);
          break;
        case 'p':
        case 'note':
          add(block.text, index, 2);
          break;
        case 'steps':
          block.items.forEach((item) => {
            const step = guideStep(item);
            add(step.why ? `${step.text} ${step.why}` : step.text, index, 2);
          });
          break;
        case 'list':
          block.items.forEach((item) => add(item, index, 2));
          break;
        case 'rows':
          block.rows.forEach((row) =>
            entries.push({
              page: page.id,
              pageTitle,
              block: index,
              text: [
                `${normalise(t(row.key))} — ${normalise(t(row.text))}`,
                ...(row.rule ? [normalise(t(row.rule))] : []),
                ...guideRowWhy(row).map((why) => normalise(t(why))),
              ].join(' '),
              rank: 2,
            }),
          );
          break;
        case 'tiles':
        case 'cards':
          block.items.forEach((item) =>
            entries.push({
              page: page.id,
              pageTitle,
              block: index,
              text: `${normalise(t(item.title))} — ${normalise(t(item.text))}`,
              rank: 2,
            }),
          );
          break;
        default:
          break;
      }
    });
  });
  return entries;
};

export const GUIDE_SEARCH_LIMIT = 12;

// substring match; best rank first, then the earliest match; one line per
// page per block so a long table does not flood the list
export const searchGuide = (
  index: GuideIndexEntry[],
  query: string,
): GuideSearchHit[] => {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const seen = new Set<string>();
  return index
    .map((entry) => ({
      ...entry,
      position: entry.text.toLowerCase().indexOf(q),
    }))
    .filter((hit) => hit.position !== -1)
    .sort((a, b) => a.rank - b.rank || a.position - b.position)
    .filter((hit) => {
      const key = `${hit.page}|${hit.block}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, GUIDE_SEARCH_LIMIT);
};

export type GuideSnippet = { before: string; match: string; after: string };

// keep the match visible in a long line, with a little of what surrounds it
export const guideSnippet = (text: string, query: string): GuideSnippet => {
  const q = query.trim().toLowerCase();
  const at = text.toLowerCase().indexOf(q);
  let start = 0;
  let prefix = '';
  if (at > 34) {
    start = at - 24;
    prefix = '…';
  }
  const cut = text.slice(start, start + 96);
  const i = cut.toLowerCase().indexOf(q);
  const suffix = start + 96 < text.length ? '…' : '';
  if (i === -1) return { before: prefix + cut + suffix, match: '', after: '' };
  return {
    before: prefix + cut.slice(0, i),
    match: cut.slice(i, i + q.length),
    after: cut.slice(i + q.length) + suffix,
  };
};
